import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Modal,
  FormControl,
  MenuItem,
  Select,
  Autocomplete,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import * as Yup from "yup";
import ModalContent from "../../../common/components/Modal";
import { useFormik } from "formik";
import { useInfiniteQuery, useQuery } from "react-query";
import { AssetApi } from "../../../services/netServices";
import { useTranslation } from "react-i18next";
import { CustomListItem } from "../../checkList/components/ListItem";
import { GlobalErrorBox } from "../../../common/components/ErrorBox";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import { CreateAssetTransactionDto } from "../../../services/netServices/models/create-asset-transaction-dto";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { transaction } from "mobx";
interface AssetModalProps {
  open: boolean;
  onClose: () => void;
  refetchList?: any;
  selectedType: number;
}

export enum TransactionTypeProp {
  Assign = "assign",
  Transfer = "transfer",
  Retire = "retire",
  Sell = "sell",
  Restock = "restock",
}
export interface ExtendedCreateAssetTransactionDto
  extends CreateAssetTransactionDto {
  TransactionDate?: number;
  Images?: Array<File>;
}

export default function TransactionModal({
  open,
  onClose,
  refetchList,
  selectedType,
}: AssetModalProps): JSX.Element {
  const limit = 10;
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const { t } = useTranslation() as any;
  const [searchAsset, setSearchAsset] = useState("");
  const [assetId, setAssetId] = useState<number>(null);
  const [debouncedSearchAsset] = useDebounce(searchAsset, 500);
  const [apiError, setApiError] = useState<string | null>(null);

  const fetchAssignees = async ({ pageParam = 1 }) => {
    const { data } = await new AssetApi().assetAssigneesGet(
      pageParam,
      limit,
      search
    );
    return {
      assignees: data.Data,
      total: data.Totals,
      nextPage: pageParam + 1,
    };
  };

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery(["assignees"], fetchAssignees, {
      getNextPageParam: (lastPage) =>
        lastPage?.assignees?.length === limit ? lastPage.nextPage : undefined,
    });

  const {
    data: assetOptions,
    refetch: refetchAssets,
    isFetching: isFetchingAssets,
  } = useQuery(
    ["assets", debouncedSearchAsset],
    async () => {
      const { data } = await new AssetApi().assetGet(
        1,
        1000,
        debouncedSearchAsset
      );
      return data?.Data ?? [];
    },
    {
      enabled: open,
      keepPreviousData: true,
    }
  );

  const { data: Assignees, refetch: refetchAssigneesId } = useQuery(
    ["/Asset/Assignees/{id}"],
    async () => {
      const { data } = await new AssetApi().assetAssigneesIdGet(
        formik?.values?.AssetId ?? 0
      );
      return data;
    },
    { enabled: false }
  );

  useEffect(() => {
    refetch();
  }, [search]);

  useEffect(() => {
    refetchAssets();
  }, []);
  const selectedAsset = useMemo(() => {
    return assetOptions?.find((opt) => opt.Id === assetId);
  }, [assetId, assetOptions]);

  const validationSchema = Yup.object({
    AssetId: Yup.number().required(t("34_00_name_required")),
    Quantity: Yup.number()
      .required(t("34_00_quantity_required"))
      .min(1, t("34_00_quantity_required")),
    TransactionType: Yup.string()
      .required()
      .test(
        "assign-target-required",
        t("34_00_group_required"),
        function (value) {
          const { EmployeeId, GroupId, DepartmentId } = this.parent;
          if (value === TransactionTypeProp.Assign) {
            return (
              EmployeeId !== null || GroupId !== null || DepartmentId !== null
            );
          }
          return true;
        }
      ),

    DepartmentId: Yup.number()
      .nullable()
      .when("TransactionType", {
        is: TransactionTypeProp.Transfer,
        then: (schema) => schema.required(t("34_00_group_required")),
        otherwise: (schema) => schema.nullable(),
      }),

    GroupId: Yup.number().nullable(),
    EmployeeId: Yup.number().nullable(),
  });

  const [initialValues] = React.useState<ExtendedCreateAssetTransactionDto>({
    AssetId: undefined,
    TransactionType:
      selectedType === 0 || selectedType === 2 || selectedType === 1
        ? TransactionTypeProp.Assign
        : selectedType === 3
        ? TransactionTypeProp.Transfer
        : selectedType === 4
        ? TransactionTypeProp.Retire
        : selectedType === 5
        ? TransactionTypeProp.Sell
        : selectedType === 6
        ? TransactionTypeProp.Restock
        : undefined,
    Quantity: 1,
    EmployeeId: null,
    GroupId: null,
    Reason: "",
    DepartmentId: null,
    ToDepartmentId: null,
    ToGroupId: null,
    ToEmployeeId: null,
    TransactionDate: dayjs().unix(),
    Images: [],
  });

  const handleSubmit = async (
    values: ExtendedCreateAssetTransactionDto
  ): Promise<void> => {
    try {
      setIsLoading(true);
      const dataLocalStorage = localStorage.getItem("USER");
      const parsedData = JSON.parse(dataLocalStorage ?? "");
      const token = parsedData?.User?.token;
      await new AssetApi().assetTransactionPost(
        token,
        values?.AssetId,
        values?.TransactionType,
        values?.Quantity,
        values?.EmployeeId ?? undefined,
        values.ToEmployeeId ?? undefined,
        values.GroupId ?? undefined,
        values.ToGroupId ?? undefined,
        values.DepartmentId ?? undefined,
        values.ToDepartmentId ?? undefined,
        values?.Reason ?? undefined,
        values?.TransactionDate ?? undefined,
        values?.Images
      );

      refetchList();
      onClose();
      toast.success(t("00_02_success_saved"));
    } catch (ex: any) {
      const msg = ex?.response?.data?.Message;
      const asset = assetOptions?.find((a) => a.Id === values.AssetId);
      if (msg === "quantity_invalid") {
        setApiError(
          t("34_00_quantity_invalid", {
            asset_name: asset.Name ?? "",
            quantity: (asset?.Quantity ?? 0) + 1,
          })
        );
      } else {
        setApiError(msg || "Unknown error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema,
  });
  useEffect(() => {
    if (formik.values.AssetId) {
      refetchAssigneesId();
    }
  }, [formik.values.AssetId, refetchAssigneesId]);

  useEffect(() => {
    if (!formik.values.AssetId) return;

    const shouldForceAssign =
      selectedAsset?.IsAssigned === false ||
      (Array.isArray(Assignees) && Assignees.length === 0);

    if (
      shouldForceAssign &&
      formik.values.TransactionType === TransactionTypeProp.Transfer
    ) {
      formik.setFieldValue("TransactionType", TransactionTypeProp.Assign);
    }
  }, [
    Assignees,
    formik.values.AssetId,
    selectedAsset?.IsAssigned,
    formik.values.TransactionType,
  ]);

  const groupOptions = useMemo(
    () => data?.pages.flatMap((page) => page.assignees) ?? [],
    [data]
  );

  const groupOptionsFiltered = useMemo(() => {
    const source =
      formik.values.TransactionType !== TransactionTypeProp.Assign
        ? Assignees
        : groupOptions;

    const result: any[] = [];

    source?.forEach((dept) => {
      // Push Department option
      result.push({
        isDepartment: true,
        DepartmentId: dept.DepartmentId,
        DepartmentName: dept.DepartmentName,
        GroupId: null,
        GroupName: `${dept.DepartmentName}`,
      });

      // Push each Group
      dept.Groups?.forEach((group) => {
        result.push({
          isDepartment: false,
          DepartmentId: dept.DepartmentId,
          DepartmentName: dept.DepartmentName,
          GroupId: group.GroupId,
          GroupName: group.GroupName,
        });
      });
    });

    return result;
  }, [formik.values.TransactionType, Assignees, groupOptions]);

  const toGroupOptionsFiltered = useMemo(() => {
    return (
      groupOptions?.flatMap((dept) => {
        const departmentHeader = {
          isDepartment: true,
          DepartmentId: dept.DepartmentId,
          DepartmentName: dept.DepartmentName,
          GroupId: null,
          GroupName: dept.DepartmentName,
        };

        const groupItems =
          dept.Groups?.map((group) => ({
            isDepartment: false,
            DepartmentId: dept.DepartmentId,
            DepartmentName: dept.DepartmentName,
            GroupId: group.GroupId,
            GroupName: group.GroupName,
          })) ?? [];

        return [departmentHeader, ...groupItems];
      }) ?? []
    );
  }, [groupOptions]);

  const employeeOptionsFiltered = useMemo(() => {
    const source =
      formik.values.TransactionType !== TransactionTypeProp.Assign
        ? Assignees
        : groupOptions;

    const allGroups = source?.flatMap((dept) => dept.Groups ?? []) ?? [];

    const matchedGroup = allGroups.find(
      (group) => group.GroupId === formik.values.GroupId
    );

    return (
      matchedGroup?.Employees?.map((emp) => ({
        id: emp.EmployeeId ?? 0,
        label: emp.EmployeeName ?? "",
      })) ?? []
    );
  }, [
    formik.values.TransactionType,
    formik.values.GroupId,
    Assignees,
    groupOptions,
  ]);

  const filterOptions = (
    options: { label: string; id: number }[],
    { inputValue }: { inputValue: string }
  ) => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase())
    );
  };
  const toEmployeeOptions = useMemo(() => {
    if (
      formik.values.TransactionType === TransactionTypeProp.Transfer &&
      formik.values.ToGroupId
    ) {
      const matchedGroup = groupOptions
        ?.flatMap((dept) => dept.Groups ?? [])
        ?.find((group) => group.GroupId === formik.values.ToGroupId);

      return (
        matchedGroup?.Employees?.map((emp) => ({
          label: emp.EmployeeName ?? "",
          id: emp.EmployeeId ?? 0,
        })) ?? []
      );
    }

    return [];
  }, [formik.values.ToGroupId, formik.values.TransactionType, groupOptions]);

  const handleScroll = (event: any) => {
    const listboxNode = event.target as HTMLElement;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >=
        listboxNode.scrollHeight - 20 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };
  React.useEffect(() => {
    const { TransactionType } = formik.values;
    if (
      (TransactionType === TransactionTypeProp.Assign || TransactionType === TransactionTypeProp.Sell) &&
      selectedAsset &&
      selectedAsset.Quantity < 1
    ) {
      setApiError(
        t("34_00_quantity_invalid", {
          asset_name: selectedAsset.Name ?? "",
          quantity: (selectedAsset?.Quantity ?? 0) + 1,
        })
      );
    } else {
      setApiError(null);
    }
  }, [formik.values.TransactionType, formik.values.AssetId]);
  return (
    <Modal open={open} onClose={onClose}>
      <Box>
        <ModalContent
          onClose={onClose}
          title={t("34_00_new_transaction")}
          width={600}
        >
          <form onSubmit={formik.handleSubmit}>
            {formik.submitCount > 0 &&
              Object.keys(formik.errors).length > 0 && (
                <GlobalErrorBox
                  errors={Object.values(formik.errors).map(
                    (e) => e?.toString() ?? ""
                  )}
                />
              )}
            {apiError && <GlobalErrorBox errors={[apiError]} />}
            <CustomListItem
              leftContainerWidth={150}
              text={t("34_00_asset_name")}
              containerStyle={{ marginBottom: "16px" }}
              RightComponent={
                <Autocomplete
                  value={selectedAsset}
                  onChange={(event, newValue) => {
                    formik.setFieldValue("AssetId", newValue?.Id ?? null);
                    setAssetId(newValue?.Id ?? null);

                    if (newValue?.Name) {
                      setSearchAsset(newValue.Name);
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    setSearchAsset(newInputValue);
                  }}
                  options={assetOptions ?? []}
                  getOptionLabel={(option) =>
                    `${option?.Name ?? ""}${
                      option?.SerialNumber ? " - " + option.SerialNumber : ""
                    }`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?.Id === value?.Id
                  }
                  fullWidth
                  loading={isFetchingAssets}
                  renderOption={(props, option, index) => (
                    <li {...props} key={`${option.Id ?? option.Name}-${index}`}>
                      {option.Name}
                      {option.SerialNumber ? ` - ${option.SerialNumber}` : ""}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      fullWidth
                      error={
                        formik.touched.AssetId && Boolean(formik.errors.AssetId)
                      }
                      onBlur={() => formik.setFieldTouched("AssetId", true)}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isFetchingAssets ? (
                              <CircularProgress size={16} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              }
            />

            <CustomListItem
              leftContainerWidth={150}
              text={t("34_00_transaction_type")}
              containerStyle={{ marginBottom: "16px" }}
              RightComponent={
                <FormControl
                  variant="standard"
                  fullWidth
                  error={
                    formik.touched.TransactionType &&
                    Boolean(formik.errors.TransactionType)
                  }
                >
                  <Select
                    value={formik.values.TransactionType}
                    onChange={(e) =>
                      formik.setFieldValue("TransactionType", e.target.value)
                    }
                    onBlur={() =>
                      formik.setFieldTouched("TransactionType", true)
                    }
                    variant="standard"
                  >
                    <MenuItem value={TransactionTypeProp.Assign}>
                      {t("34_00_assign")}
                    </MenuItem>
                    <MenuItem
                      disabled={
                        (selectedAsset &&
                          selectedAsset?.IsAssigned === false) ||
                        (formik.values.AssetId &&
                          (Assignees?.length ?? 0) === 0)
                      }
                      value={TransactionTypeProp.Transfer}
                    >
                      {t("34_00_transfer")}
                    </MenuItem>

                    <MenuItem value={TransactionTypeProp.Retire}>
                      {t("34_00_retire")}
                    </MenuItem>
                    <MenuItem value={TransactionTypeProp.Sell}>
                      {t("34_00_sell")}
                    </MenuItem>
                    <MenuItem value={TransactionTypeProp.Restock}>
                      {t("34_00_restock")}
                    </MenuItem>
                  </Select>
                </FormControl>
              }
            />

            <CustomListItem
              leftContainerWidth={150}
              text={t("00_06_quantity")}
              containerStyle={{ marginBottom: "16px" }}
              RightComponent={
                <NumericFormat
                  customInput={TextField}
                  variant="standard"
                  thousandSeparator=","
                  allowNegative={false}
                  value={formik.values.Quantity}
                  onValueChange={(values) => {
                    const { floatValue } = values;
                    formik.setFieldValue("Quantity", floatValue ?? "");
                  }}
                  allowLeadingZeros={false}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              }
            />
            <>
              {formik.values.TransactionType ===
                TransactionTypeProp.Transfer && (
                <>
                  <>
                    <Typography
                      color="#636363"
                      sx={{ textTransform: "uppercase" }}
                      mb={1}
                    >
                      {t("34_00_from")}
                    </Typography>
                    <Box
                      p={2}
                      mb={4}
                      sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}
                    >
                      <CustomListItem
                        leftContainerWidth={150}
                        text={t("34_00_group")}
                        containerStyle={{ marginBottom: "16px" }}
                        RightComponent={
                          <Autocomplete
                            value={
                              groupOptionsFiltered.find(
                                (opt) =>
                                  opt.GroupId === formik.values.GroupId &&
                                  opt.DepartmentId ===
                                    formik.values.DepartmentId
                              ) ?? null
                            }
                            disablePortal
                            fullWidth
                            options={groupOptionsFiltered}
                            noOptionsText={t("00_06_no_data")}
                            getOptionLabel={(option) => option.GroupName}
                            onChange={(event, newValue) => {
                              if (!newValue) {
                                formik.setFieldValue("GroupId", null);
                                formik.setFieldValue("DepartmentId", null);
                                return;
                              }
                              if (newValue.isDepartment) {
                                formik.setFieldValue(
                                  "DepartmentId",
                                  newValue.DepartmentId
                                );
                                formik.setFieldValue("GroupId", null);
                              } else {
                                formik.setFieldValue(
                                  "GroupId",
                                  newValue.GroupId
                                );
                                formik.setFieldValue(
                                  "DepartmentId",
                                  newValue.DepartmentId
                                );
                              }
                            }}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                key={`${
                                  option.isDepartment
                                    ? "dept-" + option.DepartmentId
                                    : "group-" + option.GroupId
                                }`}
                                style={
                                  option.isDepartment
                                    ? {
                                        fontSize: "14px",
                                        textTransform: "uppercase",
                                        fontWeight: 500,
                                        color: "rgba(0,0,0,0.54)",
                                        padding: "16px",
                                      }
                                    : {
                                        paddingLeft: "32px",
                                        color: "rgba(0,0,0,0.87)",
                                        height: "48px",
                                      }
                                }
                              >
                                {option.GroupName}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                fullWidth
                                onChange={(e) =>
                                  handleSearchChange(e.target.value)
                                }
                              />
                            )}
                            ListboxProps={{
                              onScroll: handleScroll,
                              style: {
                                maxHeight: "300px",
                                overflowY: "auto",
                              },
                            }}
                            classes={{ groupLabel: "autocomplete-group-label" }}
                            loading={isFetchingNextPage}
                            loadingText={<CircularProgress size={20} />}
                          />
                        }
                      />
                      <CustomListItem
                        leftContainerWidth={150}
                        text={t("34_00_employee")}
                        containerStyle={{ marginBottom: "16px" }}
                        RightComponent={
                          <Autocomplete
                            value={
                              employeeOptionsFiltered.find(
                                (emp) => emp.id === formik.values.EmployeeId
                              ) ?? null
                            }
                            onChange={(event, newValue) => {
                              formik.setFieldValue(
                                "EmployeeId",
                                newValue?.id ?? null
                              );
                            }}
                            options={employeeOptionsFiltered as any}
                            getOptionLabel={(option) => option.label ?? ""}
                            filterOptions={filterOptions}
                            fullWidth
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                fullWidth
                                sx={{
                                  "& .MuiInput-underline.Mui-disabled:before": {
                                    borderBottom: "1px solid #e5e5e5",
                                  },
                                  "& .MuiAutocomplete-endAdornment": {
                                    paddingRight: "2px",
                                  },
                                }}
                              />
                            )}
                            disabled={!formik.values.GroupId}
                          />
                        }
                      />
                    </Box>
                  </>
                  <>
                    <Typography
                      color="#636363"
                      sx={{ textTransform: "uppercase" }}
                      mb={1}
                    >
                      {t("34_00_to")}
                    </Typography>
                    <Box
                      p={2}
                      mb={4}
                      sx={{ border: "1px solid #e0e0e0", borderRadius: 1 }}
                    >
                      <CustomListItem
                        leftContainerWidth={150}
                        text={t("34_00_to_group")}
                        containerStyle={{ marginBottom: "16px" }}
                        RightComponent={
                          <Autocomplete
                            value={
                              toGroupOptionsFiltered.find(
                                (opt) =>
                                  opt.GroupId === formik.values.ToGroupId &&
                                  opt.DepartmentId ===
                                    formik.values.ToDepartmentId
                              ) ?? null
                            }
                            disablePortal
                            fullWidth
                            options={toGroupOptionsFiltered}
                            getOptionLabel={(option) => option.GroupName}
                            onChange={(event, newValue) => {
                              if (!newValue) {
                                formik.setFieldValue("ToGroupId", null);
                                formik.setFieldValue("ToDepartmentId", null);
                                return;
                              }
                              if (newValue.isDepartment) {
                                formik.setFieldValue(
                                  "ToDepartmentId",
                                  newValue.DepartmentId
                                );
                                formik.setFieldValue("ToGroupId", null);
                              } else {
                                formik.setFieldValue(
                                  "ToGroupId",
                                  newValue.GroupId
                                );
                                formik.setFieldValue(
                                  "ToDepartmentId",
                                  newValue.DepartmentId
                                );
                              }
                            }}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                key={`${
                                  option.isDepartment
                                    ? "dept-" + option.DepartmentId
                                    : "group-" + option.GroupId
                                }`}
                                style={
                                  option.isDepartment
                                    ? {
                                        fontSize: "14px",
                                        textTransform: "uppercase",
                                        fontWeight: 500,
                                        color: "rgba(0,0,0,0.54)",
                                        padding: "16px",
                                      }
                                    : {
                                        paddingLeft: "32px",
                                        color: "rgba(0,0,0,0.87)",
                                        height: "48px",
                                      }
                                }
                              >
                                {option.GroupName}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                fullWidth
                                onChange={(e) =>
                                  handleSearchChange(e.target.value)
                                }
                              />
                            )}
                            ListboxProps={{
                              onScroll: handleScroll,
                              style: {
                                maxHeight: "300px",
                                overflowY: "auto",
                              },
                            }}
                            loading={isFetchingNextPage}
                            loadingText={<CircularProgress size={20} />}
                          />
                        }
                      />
                      <CustomListItem
                        leftContainerWidth={150}
                        text={t("34_00_to_employee")}
                        containerStyle={{ marginBottom: "16px" }}
                        RightComponent={
                          <Autocomplete
                            value={
                              toEmployeeOptions.find(
                                (emp) => emp.id === formik.values.ToEmployeeId
                              ) ?? null
                            }
                            onChange={(event, newValue) => {
                              formik.setFieldValue(
                                "ToEmployeeId",
                                newValue?.id ?? null
                              );
                            }}
                            options={toEmployeeOptions}
                            getOptionLabel={(option) => option.label ?? ""}
                            filterOptions={filterOptions}
                            fullWidth
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                fullWidth
                                sx={{
                                  "& .MuiInput-underline.Mui-disabled:before": {
                                    borderBottom: "1px solid #e5e5e5",
                                  },
                                  "& .MuiAutocomplete-endAdornment": {
                                    paddingRight: "2px",
                                  },
                                }}
                              />
                            )}
                            disabled={!formik.values.ToGroupId}
                          />
                        }
                      />
                    </Box>
                  </>
                </>
              )}
            </>

            {!(
              (formik.values.TransactionType === TransactionTypeProp.Retire &&
                selectedAsset?.IsAssigned === false) ||
              formik.values.TransactionType === TransactionTypeProp.Restock ||
              formik.values.TransactionType === TransactionTypeProp.Transfer
            ) && (
              <CustomListItem
                leftContainerWidth={150}
                text={t("34_00_group")}
                containerStyle={{ marginBottom: "16px" }}
                RightComponent={
                  <Autocomplete
                    value={
                      groupOptionsFiltered.find(
                        (opt) =>
                          opt.GroupId === formik.values.GroupId &&
                          opt.DepartmentId === formik.values.DepartmentId
                      ) ?? null
                    }
                    disablePortal
                    fullWidth
                    options={groupOptionsFiltered}
                    noOptionsText={t("00_06_no_data")}
                    getOptionLabel={(option) => option.GroupName}
                    onChange={(event, newValue) => {
                      if (!newValue) {
                        formik.setFieldValue("GroupId", null);
                        formik.setFieldValue("DepartmentId", null);
                        return;
                      }
                      if (newValue.isDepartment) {
                        formik.setFieldValue(
                          "DepartmentId",
                          newValue.DepartmentId
                        );
                        formik.setFieldValue("GroupId", null);
                      } else {
                        formik.setFieldValue("GroupId", newValue.GroupId);
                        formik.setFieldValue(
                          "DepartmentId",
                          newValue.DepartmentId
                        );
                      }
                    }}
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        key={`${
                          option.isDepartment
                            ? "dept-" + option.DepartmentId
                            : "group-" + option.GroupId
                        }`}
                        style={
                          option.isDepartment
                            ? {
                                fontSize: "14px",
                                textTransform: "uppercase",
                                fontWeight: 500,
                                color: "rgba(0,0,0,0.54)",
                                padding: "16px",
                              }
                            : {
                                paddingLeft: "32px",
                                color: "rgba(0,0,0,0.87)",
                                height: "48px",
                              }
                        }
                      >
                        {option.GroupName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        fullWidth
                        onChange={(e) => handleSearchChange(e.target.value)}
                      />
                    )}
                    ListboxProps={{
                      onScroll: handleScroll,
                      style: {
                        maxHeight: "300px",
                        overflowY: "auto",
                      },
                    }}
                    classes={{ groupLabel: "autocomplete-group-label" }}
                    loading={isFetchingNextPage}
                    loadingText={<CircularProgress size={20} />}
                  />
                }
              />
            )}

            {!(
              (formik.values.TransactionType === TransactionTypeProp.Retire &&
                selectedAsset?.IsAssigned === false) ||
              formik.values.TransactionType === TransactionTypeProp.Restock ||
              formik.values.TransactionType === TransactionTypeProp.Transfer
            ) && (
              <CustomListItem
                leftContainerWidth={150}
                text={t("34_00_employee")}
                containerStyle={{ marginBottom: "16px" }}
                RightComponent={
                  <Autocomplete
                    value={
                      employeeOptionsFiltered.find(
                        (emp) => emp.id === formik.values.EmployeeId
                      ) ?? null
                    }
                    onChange={(event, newValue) => {
                      formik.setFieldValue("EmployeeId", newValue?.id ?? null);
                    }}
                    options={employeeOptionsFiltered as any}
                    getOptionLabel={(option) => option.label ?? ""}
                    filterOptions={filterOptions}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        fullWidth
                        sx={{
                          "& .MuiInput-underline.Mui-disabled:before": {
                            borderBottom: "1px solid #e5e5e5",
                          },
                          "& .MuiAutocomplete-endAdornment": {
                            paddingRight: "2px",
                          },
                        }}
                      />
                    )}
                    disabled={!formik.values.GroupId}
                  />
                }
              />
            )}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <CustomListItem
                leftContainerWidth={150}
                containerStyle={{ marginBottom: "16px" }}
                text={t("34_00_transaction_date")}
                RightComponent={
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={
                      formik.values.TransactionDate
                        ? dayjs(formik.values.TransactionDate * 1000)
                        : null
                    }
                    onChange={(value) => {
                      if (value) {
                        const timestampSeconds = Math.floor(
                          value.set("millisecond", 0).valueOf() / 1000
                        );
                        formik.setFieldValue(
                          "TransactionDate",
                          timestampSeconds
                        );
                      } else {
                        formik.setFieldValue("TransactionDate", 0);
                      }
                    }}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        fullWidth: true,
                        sx: {
                          "& .MuiIconButton-root": {
                            marginRight: "-4px",
                          },
                        },
                      },
                    }}
                  />
                }
              />
            </LocalizationProvider>
            <CustomListItem
              leftContainerWidth={150}
              text={t("34_00_upload_photo")}
              containerStyle={{ marginBottom: "16px" }}
              RightComponent={
                <Box width="100%">
                  <Box
                    component="label"
                    htmlFor="multi-file-upload"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "end",
                      borderRadius: 1,
                      borderBottom: "1px solid rgba(0,0,0,0.12)",
                      padding: 1,
                    }}
                  >
                    <UploadFileIcon fontSize="medium" color="action" />
                  </Box>

                  <input
                    type="file"
                    id="multi-file-upload"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={async (e) => {
                      const files = e.target.files;
                      if (!files) return;

                      // Hàm resize ảnh
                      const resizeImage = (
                        file: File,
                        maxWidth = 800,
                        maxHeight = 800
                      ) => {
                        return new Promise<File>((resolve) => {
                          const img = new Image();
                          img.src = URL.createObjectURL(file);
                          img.onload = () => {
                            let { width, height } = img;
                            if (width > height) {
                              if (width > maxWidth) {
                                height = (height * maxWidth) / width;
                                width = maxWidth;
                              }
                            } else {
                              if (height > maxHeight) {
                                width = (width * maxHeight) / height;
                                height = maxHeight;
                              }
                            }
                            const canvas = document.createElement("canvas");
                            canvas.width = width;
                            canvas.height = height;
                            const ctx = canvas.getContext("2d");
                            ctx?.drawImage(img, 0, 0, width, height);
                            canvas.toBlob((blob) => {
                              resolve(
                                new File([blob!], file.name, {
                                  type: file.type,
                                })
                              );
                            }, file.type);
                          };
                        });
                      };

                      const resizedFiles = await Promise.all(
                        Array.from(files).map((f) => resizeImage(f))
                      );
                      formik.setFieldValue("Images", [
                        ...(formik.values.Images || []),
                        ...resizedFiles,
                      ]);
                    }}
                  />

                  {formik.values.Images?.length > 0 && (
                    <Box
                      mt={2}
                      display="grid"
                      gridTemplateColumns={
                        formik.values.Images.length === 1
                          ? "1fr"
                          : "repeat(auto-fill, minmax(100px, 1fr))"
                      }
                      gap={2}
                    >
                      {formik.values.Images.map((file, idx) => {
                        const imgUrl = URL.createObjectURL(file);
                        return (
                          <Box
                            key={idx}
                            position="relative"
                            sx={{
                              borderRadius: 1,
                              border: "1px solid #ccc",
                              maxWidth: 400,
                              maxHeight: 300,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              component="img"
                              src={imgUrl}
                              alt={`image-${idx}`}
                              sx={{
                                width: "100%",
                                height: 80,
                                objectFit: "cover",
                                display: "block",
                              }}
                              onLoad={() => URL.revokeObjectURL(imgUrl)} // giải phóng bộ nhớ
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: "absolute",
                                top: 4,
                                right: 4,
                                backgroundColor: "rgba(255,255,255,0.8)",
                                "&:hover": {
                                  backgroundColor: "rgba(255,255,255,1)",
                                },
                              }}
                              onClick={() => {
                                const updatedValue = [...formik.values.Images];
                                updatedValue.splice(idx, 1);
                                formik.setFieldValue("Images", updatedValue);
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              }
            />

            <CustomListItem
              leftContainerWidth={150}
              text={
                formik.values.TransactionType === TransactionTypeProp.Restock ||
                formik.values.TransactionType === TransactionTypeProp.Sell
                  ? t("00_06_note")
                  : t("12_03_reason")
              }
              containerStyle={{ marginBottom: "16px" }}
              RightComponent={
                <TextField
                  value={formik.values.Reason}
                  onChange={(e) =>
                    formik.setFieldValue("Reason", e.target.value)
                  }
                  variant="standard"
                  fullWidth
                />
              }
            />

            <Box display="flex" justifyContent="end" gap={4} mt={5}>
              <Button
                variant="outlined"
                style={{ borderColor: "#e5e5e5", color: "#000" }}
                onClick={onClose}
              >
                {t("00_03_cancel")}
              </Button>
              <Button
                variant="contained"
                style={{ color: "#fff", backgroundColor: "#08B0A0" }}
                disabled={isLoading}
                type="submit"
              >
                {t("00_03_save")}
              </Button>
            </Box>
          </form>
        </ModalContent>
      </Box>
    </Modal>
  );
}
